/**
 * 表示の広告（Adsterra の通常バナー・Social Bar）の ON / OFF（2026-10-19 creator/ads-open）。概要ページの機能の一覧の下に置く。
 *
 * ON にすると KUMODeck の配信が公開中のページに KUMODeck 共通の広告の枠のタグを入れる（作り手はコードを書かない・待ちは無い）。
 * 判断（config の書き換え・出すか）は displayAds.ts、配信の差し込み・数え・配分はサーバーの modules/ads/displayAds.ts。
 * WHY 機能の一覧の行ではなく専用のカードか: displayAds.ts の冒頭（CLI・MCP の一覧を変えない・収益の決まりを正直に見せる場所が要る）。
 *
 * 切り替えは機能の一覧（FeaturesCard）と同じ作法: 確認ダイアログ（本番は slug を打つ）→ 設定のページと同じ PUT …/config →
 * 手元の kumo.config.json にも同じ変更を、の案内（CLI の features on では入らない = ファイルの行を出す）。
 * 広告が出るのは公開版だけなので、テスト版の画面では切り替えを出さず「公開版に切り替えて」とだけ出す。
 *
 * Social Bar の説明（2026-10-19 founder「入れたければ入れればいい・クリエイターが選べる・どういう広告かは説明する」）:
 *   2 つの行それぞれに違いが分かる 1〜2 行。Social Bar の行の下に「どんな広告か」（事実・良い点・気になる点・決めるのはあなた）を畳んで置き、
 *   ON にする確認のダイアログでは同じものを開いたまま出す（カードを開かずに押しても、選ぶ前に必ず目に入る）。押し付けない・脅さない文（i18n）。
 *
 * 作り手ごとの決まり（2026-10-27 console/earn-ads-card・displayAds.ts adsCardMode の WHY）:
 *   「初日から稼げる」の試しの作り手（GET /v1/money/earn が 200）には、KUMODeck 自身の広告・1 プレイの額・週の入れ物・30 日の確認の後の
 *   Amazon ギフトカード・詳しくは Play earnings を出す（告知の文と同じ言い方・額はサーバーの値）。旗 house の範囲の中で試しが動いていない作り手には
 *   「KUMODeck の短い宣伝だけ」。どちらも Adsterra の文（100%・概算・60 日）と Social Bar の行・絵は出さない（その作り手のゲームには出ない）。
 *   範囲の外の作り手（両方 404）は、サーバーが今までどおり Adsterra を出すので前の説明のまま。
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, envPath, type ApiError, type HostingView } from '../api';
import { DISPLAY_AD_KINDS, SOCIAL_BAR_FACTS, adsCardMode, displayAdsState, kumoAdsOnly, shownKinds, signalOf, withDisplayAd, type DisplayAdKind } from '../displayAds';
import { strongConfirmOk } from '../featureToggle';
import { useResource } from '../hooks';
import { errorText, intlLocale, useT } from '../i18n';
import { PLAY_EARN_API, PLAY_EARN_PATH, ratePerPlay, wholeUsd, type PlayEarnView } from '../playEarn';
import { houseHostPrefPath } from '../selfAdsApi';
import { CLI_NAME } from '../cliCommand';
import { ConfirmDialog, ErrorState, Spinner } from './ui';
import { DisplayAdsPictures } from './DisplayAdsPictures';
import { SelfAdHostChoice } from './SelfAdHostChoice';
import { HouseAdsChoice } from './HouseAdsChoice';

type Env = 'development' | 'production';

/** Social Bar の事実と良い点 / 気になる点（カードの畳みと確認のダイアログで同じもの） */
function SocialBarFacts() {
  const { t } = useT();
  return (
    <div className="social-bar-facts small">
      <ul>
        {SOCIAL_BAR_FACTS.map((k) => (
          <li key={k}>{t(`displayAds.sb.${k}`)}</li>
        ))}
      </ul>
      <dl className="social-bar-weigh">
        <div>
          <dt>{t('displayAds.sb.goodLabel')}</dt>
          <dd>{t('displayAds.sb.good')}</dd>
        </div>
        <div>
          <dt>{t('displayAds.sb.considerLabel')}</dt>
          <dd>{t('displayAds.sb.consider')}</dd>
        </div>
      </dl>
      <p className="muted">{t('displayAds.sb.yours')}</p>
    </div>
  );
}

/**
 * 試しの作り手の決まりの行（カードの下と、ON にする確認のダイアログで同じもの）。額・日数は GET /v1/money/earn の値。
 * 1 プレイの額は告知の文の書き方（"1¢"）。ちょうどのセントでない設定の日は Play earnings の画面と同じ "$0.0063" の形
 */
function earnWords(v: PlayEarnView, t: ReturnType<typeof useT>['t']) {
  const loc = intlLocale();
  return {
    rate: Number.isInteger(v.centsPerPlay) ? t('pe.card.rate', { n: v.centsPerPlay }) : ratePerPlay(v.centsPerPlay, loc),
    pool: wholeUsd(v.poolCents, loc),
    cap: wholeUsd(v.creatorWeeklyCapCents, loc),
    days: v.holdDays
  };
}

function EarnRules({ v }: { v: PlayEarnView }) {
  const { t } = useT();
  const w = earnWords(v, t);
  return (
    <>
      <li>{t('pe.card.play')}</li>
      <li>{t('pe.card.pot', { pool: w.pool, cap: w.cap })}</li>
      <li>{t('pe.card.paid', { days: w.days })}</li>
    </>
  );
}

export function DisplayAdsCard({ projectId, slug, env }: { projectId: string; slug: string; env: Env }) {
  const base = envPath(projectId, env);
  const { t, tr } = useT();
  const r = useResource<{ version: number; config: unknown }>(`${base}/config`, 30_000);
  // 公開中のページのホスト（<slug>.<配信ドメイン>）を「どこに出るか」に出す。読めなければ slug だけ
  const hosting = useResource<HostingView>(`${envPath(projectId, 'production')}/hosting`);
  const host = hosting.data?.hostUrl ? new URL(hosting.data.hostUrl).host : slug;
  const [plan, setPlan] = useState<{ kind: DisplayAdKind; on: boolean } | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ kind: DisplayAdKind; on: boolean } | null>(null);

  // どの決まりで説明するか（displayAds.ts adsCardMode）。どちらの口も入っていない作り手には 404 = 「口があることも見せない」のサーバーの決まりどおり
  const earn = useResource<PlayEarnView>(PLAY_EARN_API);
  const house = useResource<unknown>(houseHostPrefPath(projectId));
  const mode = adsCardMode(signalOf(earn), signalOf(house));
  const kumoOnly = kumoAdsOnly(mode);
  const ev = mode === 'earn' ? earn.data! : null;
  const playEarnLink: ReactNode = <Link to={PLAY_EARN_PATH}>{t('pe.nav')}</Link>;

  const state = r.data ? displayAdsState(r.data.config) : null;
  // KUMODeck 自身の広告だけの間は Social Bar を置かない（サーバーの houseBand）= ON の札はバナーだけで決める
  const anyOn = Boolean(state && (state.banner || (!kumoOnly && state.socialBar)));
  const kinds = state ? shownKinds(mode, state) : [];
  const name = (k: DisplayAdKind) => t(`displayAds.name.${k}`);
  const title = kumoOnly ? t('displayAds.title.kumo') : t('displayAds.title');

  const apply = async () => {
    if (!plan) return;
    setBusy(true);
    setErr(null);
    try {
      // 反映の直前に今の config を取り直す（画面を開いた後の push の上に重ねる = FeaturesCard と同じ）
      const cur = await api<{ version: number; config: unknown }>(`${base}/config`);
      await api(`${base}/config`, { method: 'PUT', body: withDisplayAd(cur.config, plan.kind, plan.on) });
      setDone(plan);
      setPlan(null);
      r.reload();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-label={title}>
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        {state && <span className={`pill ${anyOn ? 'pill-ok' : ''}`}>{anyOn ? t('features.on') : t('features.off')}</span>}
      </div>
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={title} onRetry={r.reload} />}
      {state && (
        <>
          {anyOn ? (
            <div className="alert alert-ok" role="status">
              <strong>{kumoOnly ? t('displayAds.onTitle.kumo') : t('displayAds.onTitle')}</strong>
              {ev ? <span>{tr('pe.card.on', { rate: earnWords(ev, t).rate, link: playEarnLink })}</span> : mode === 'external' ? <span>{t('displayAds.share')}</span> : null}
            </div>
          ) : (
            <p className="muted small">
              {ev ? t('pe.card.body', { rate: earnWords(ev, t).rate }) : mode === 'house' ? t('displayAds.house.body') : t('displayAds.body')}
            </p>
          )}
          {/* 見た目の違いの絵（2026-10-19 design/visual-guides）: 文の説明より先に「どこに出るか」を見せる。ON / OFF・環境に関係なく出す。
              KUMODeck 自身の広告だけの間はバナーの絵だけ（Social Bar は出ない = 「(1) New Message!」の絵を見せると違う話になる） */}
          <DisplayAdsPictures bannerOnly={kumoOnly} />
          {done && !done.on && <p className="small">{t('displayAds.offTitle', { name: name(done.kind) })}</p>}
          {done && (
            <p className="small muted">
              {tr('features.localManual', { path: <code>{DISPLAY_AD_KINDS.find((k) => k.kind === done.kind)!.configPath}</code>, value: <code>{String(done.on)}</code> })}{' '}
              {tr('features.localManualThen', { cmd: <code>{`${CLI_NAME} config push --env ${env}`}</code> })}
            </p>
          )}

          {env !== 'production' ? (
            <p className="alert alert-info">{t('displayAds.devOnly', { env: t('env.production') })}</p>
          ) : !state.hosting ? (
            <p className="alert alert-info">{t('displayAds.needsHosting')}</p>
          ) : (
            <ul className="feature-list">
              {DISPLAY_AD_KINDS.filter(({ kind }) => kinds.includes(kind)).map(({ kind, configPath }) => {
                const on = state[kind];
                // KUMODeck 自身の広告だけの間に残す Social Bar の行（既に ON の時だけ = OFF にする道）は、Adsterra の説明の代わりに「いまは出ない」
                const sbHouse = kind === 'socialBar' && kumoOnly;
                return (
                  <li key={kind} className="feature-row">
                    <span className={`pill ${on ? 'pill-ok' : ''}`}>{on ? t('features.on') : t('features.off')}</span>
                    <div className="feature-name">
                      <strong>{name(kind)}</strong>
                      <div className="muted small display-ads-kind">{sbHouse ? t('displayAds.kind.socialBarHouse') : t(`displayAds.kind.${kind}`)}</div>
                      <code className="small feature-path">{configPath}</code>
                    </div>
                    <div className="feature-action">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setPlan({ kind, on: !on });
                          setTyped('');
                          setErr(null);
                          setDone(null);
                        }}
                      >
                        {on ? t('features.turnOff') : t('features.turnOn')}
                      </button>
                    </div>
                    {/* 行の下の全幅（名前の列の中に置くと 375px で「Turn on」が長い説明の下まで押し出された = 見た目の確認 2026-10-19） */}
                    {/* 通常バナーが ON なら「どの広告を出すか」（自分のゲームの広告・2026-10-22 money/self-serve-ads-dash）。
                        フラグ off のサーバーでは SelfAdHostChoice が何も出さない = 今までのカードのまま */}
                    {kind === 'banner' && on && <SelfAdHostChoice projectId={projectId} />}
                    {kind === 'socialBar' && !sbHouse && (
                      <details className="social-bar-more">
                        <summary className="small">{t('displayAds.sb.more')}</summary>
                        <SocialBarFacts />
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {/* 旗 house の間の「{product} の広告を出す / 出さない」（帯・リワード・2026-10-24 house-optout-fill）。旗 house でないサーバーでは何も出さない。
              本番の画面だけ（広告が出るのは公開版だけ = 上の切り替えと同じ線） */}
          {env === 'production' && state.hosting && <HouseAdsChoice projectId={projectId} earn={mode === 'earn'} />}

          {/* お金の決まりは ON / OFF に関係なく出す = ON にする前に分かる。作り手ごとに違う（adsCardMode）:
              earn = 試しの決まり（1 人 1 日 1 回・週の入れ物と山分けと上限・30 日の確認の後のギフトカード・詳しくは Play earnings）
              external = 前の Adsterra の決まり（100%・概算・確定と 60 日）。house / loading = お金の行は出さない（どこに出るかだけ） */}
          <ul className="small muted display-ads-notes">
            {ev && <EarnRules v={ev} />}
            {ev && <li>{tr('pe.card.more', { link: playEarnLink })}</li>}
            {mode === 'external' && (
              <>
                {anyOn ? null : <li>{t('displayAds.share')}</li>}
                <li>{t('displayAds.estimate')}</li>
                <li>{t('displayAds.when')}</li>
              </>
            )}
            <li>{t('displayAds.where', { host })}</li>
            {ev && <li>{t('pe.card.amazon')}</li>}
          </ul>
        </>
      )}

      <ConfirmDialog
        open={!!plan}
        title={plan ? t(plan.on ? 'displayAds.confirmOnTitle' : 'displayAds.confirmOffTitle', { name: name(plan.kind) }) : t('displayAds.title')}
        confirmLabel={plan?.on ? t('features.confirmOn', { env: t('env.inline.production') }) : t('features.confirmOff', { env: t('env.inline.production') })}
        tone="danger"
        busy={busy}
        confirmDisabled={!strongConfirmOk(typed, slug)}
        onClose={() => {
          if (!busy) setPlan(null);
        }}
        onConfirm={() => void apply()}
      >
        {plan && (
          <>
            <p>{t(plan.on ? 'displayAds.confirmOnBody' : 'displayAds.confirmOffBody')}</p>
            {plan.on && plan.kind === 'socialBar' && <SocialBarFacts />}
            {plan.on && ev && (
              <ul className="small display-ads-confirm-notes">
                <EarnRules v={ev} />
              </ul>
            )}
            {plan.on && mode === 'external' && (
              <ul className="small display-ads-confirm-notes">
                <li>{t('displayAds.share')}</li>
                <li>{t('displayAds.estimate')}</li>
                <li>{t('displayAds.when')}</li>
              </ul>
            )}
            <label className="field">
              <span>{tr('features.typeSlug', { slug: <code>{slug}</code> })}</span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-invalid={typed.length > 0 && !strongConfirmOk(typed, slug)}
              />
            </label>
          </>
        )}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}
